import { SetOptional } from "type-fest";
import find from 'lodash/find';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import entries from 'lodash/entries';
import mapValues from 'lodash/mapValues';
import identify from "~/identify";
import { PreparedSubview, Subview } from "./types/subview.types";
import { PreparedField, FieldOptionsPrepared, PreparedFieldBase, FieldType } from "./types/field.types";
import { hasTarget, iterateGridElements } from '~/data/targetGrid';
import { Model } from "./types/model.types";
import { ColPrepared, Grid, Row, RowPrepared } from "./types/grid.types";
import reduce from "lodash/reduce";
import size from "lodash/size";
/**
 * 
 * SUBVIEWS
 * 
 * 
 */
export const prepareSubviews = (subviews) => {
    return keyBy(map(subviews, prepareSubview), 'name') 
};

export const prepareSubview = (subview: Subview) => {
    let name = identify.subview(subview);

    return {
        ...subview,
        group: subview.group ? subview.group : identify.DEFAULT_SUBVIEW_GROUP,
        name,
    };
};


/**
 * 
 * GRID
 * 
 * 
 */


export const prepareGrid = (model: Model, subviews: Record<string, PreparedSubview> | never[] = []) => {
    return mapValues(model.fieldGrid, (grid, name) =>
        prepareGridGroup(model, grid, name === 'default' ? subviews : [])
    );
};


const prepareGridGroup = (model: Model, grid: Grid, subviews: Record<string, PreparedSubview> | never[] = []) => {
    let rows = grid.rows,
        defaultLevel = model.defaultLevel;

    if (Array.isArray(grid.rows) && (grid.rows).length < 1) {
        rows = convertGroupsToGrid(model.fieldGroups, defaultLevel);
    }

    const shouldCreateSubviewDefaultGroup =
        find(subviews, { group: identify.DEFAULT_SUBVIEW_GROUP }) && !hasTarget(rows, identify.DEFAULT_SUBVIEW_GROUP);
    if (shouldCreateSubviewDefaultGroup) {
        // because we split the screen only have half the amount of elements per group
        if (Array.isArray(grid.rows) && grid.rows.length < 1) {
            rows = convertGroupsToGrid(model.fieldGroups, defaultLevel, { lg: 3, md: 4, sm: 12, xs: 12, toggle: false });
        }

        rows = [
            {
                cols: [
                    { xs: 12, rows: rows },
                    {
                        xs: 12,
                        rows: [
                            {
                                name: identify.DEFAULT_SUBVIEW_GROUP,
                                xs: 12,
                                cols: [],
                                style: { level: defaultLevel },
                                toggle: false,
                            },
                        ],
                    },
                ],
            },
        ];
    }

    rows = prepareRows(rows);
    return {
        ...grid,
        rows,
        maxLevel: getGridMaxLevel(rows),
    };
};

const convertGroupsToGrid = (groups, level, options = {}) => {
    let defaults = { lg: 3, md: 4, sm: 12, xs: 12, toggle: false };

    options = { ...defaults, ...options };

    return groups.map(({ name, title, open }) => {
        return { name, title, open, level, ...options };
    });
};

export const getGridMaxLevel = (grid) => {
    let maxLevel = 0;

    iterateGridElements(grid, (type, obj) => {
        if (obj.level > maxLevel) {
            maxLevel = obj.level;
        }
        if (obj.style && obj.style.level > maxLevel) {
            maxLevel = obj.style.level;
        }
    });

    return maxLevel;
};


export const prepareRows = (rows: Grid['rows']) => Object.fromEntries(
    Object.entries(rows).map(([key, row]) => {
        const preparedRow = row.type === 'tabs'
        ? prepareTabRow(row, key)
        : prepareRow(row, key);
    
        return [preparedRow.key ??key, preparedRow];
    })
) as Record<string | number, Row>;


const prepareTabRow = (row, rowKey) => ({
    ...row,
    key: `tab__${rowKey}`,
    tabField: row.tabField,
    tabGroup: row.tabGroupName,
    variant: row.displayAs,
    visibleConditions: row.visible_conditions,
    tabs: mapValues(row.tabs, (tab, key) => ({
        ...tab,
        urlKey: key + '',
        key: `${rowKey}__${(tab.title ?? '')}___${key}`,
        visibleConditions: tab.visible_conditions,
        disableConditions: tab.disable_conditions,
        rows: prepareRows(tab.rows)
    }))
})

const prepareRow = (row, rowKey) => ({
    ...row,
    key: rowKey,
    type: row.type || 'row',
    visibleConditions: row.visible_conditions,
    cols: row.cols?.map((col, key) => ({
        ...col, 
        key,
        visibleConditions: col.visible_conditions,
        type: 'col',
        rows: prepareRows(col.rows)
    })) ?? []
})


export const prepareGridVisibilityFields = (grid: Record<string, { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }>) => {
    const deriveFields = (grid: { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }): string[] => {
        return Object.values(grid.rows).reduce((fields: string[], row) => {

            fields.push(...(!row.visibleConditions || typeof row.visibleConditions !== "object" ? [] : Object.keys(row.visibleConditions) as string[]))

            if ("cols" in row) {
                const colFields = row.cols.flatMap((col) => Object.values(deriveFields(col))).flat();
                fields.push(...colFields);
            }
            if ("tabs" in row) {
                const tabFields = Object.values(row.tabs ?? []).flatMap((tab) => Object.values(deriveFields(tab)).flat());
                fields.push(...tabFields);
            }

            return Array.from(new Set(fields));

        }, !grid.visibleConditions || typeof grid.visibleConditions !== "object" ? [] : Object.keys(grid.visibleConditions) as string[]);
    };

    return mapValues(grid, (row) => deriveFields(row));
}


export const prepareGridControlGroups = (grid: Record<string, { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }>) => {
    const deriveGroups = (grid: { rows: RowPrepared[] | Record<number | string, RowPrepared>, visibleConditions?: boolean | Record<string, any> }) => {
        return Object.values(grid.rows).reduce((prepared: Record<string, string[]>, row) => {
            if ('name' in row) {
                prepared.sources.push(row.name);
                return prepared;
            }

            if ("tabs" in row) {
                const tabFields = Object.fromEntries(
                    Object.values(row.tabs ?? [])
                        .map((tab) => [tab.key, deriveGroups(tab).sources ])
                ) as Record<string, string[]>;
                return {
                    ...prepared,
                    ...tabFields,
                    sources: [...prepared.sources, ...Object.values(tabFields).flat()]
                }
            }

            if ("cols" in row) {
                return row.cols.reduce((resolved, col) => {
                    const derivedGroups = deriveGroups(col);
                    return {
                        ...resolved, ...derivedGroups, sources: [...resolved.sources, ...derivedGroups.sources]
                    };
                }, prepared);
            }

            return prepared;

        }, { sources: [] });
    };

    return mapValues(grid, (row) => deriveGroups(row));
}




/**
 * 
 * 
 * FIELDS
 * 
 */
export const prepareField = (field: FieldType) => {
    let prepared: PreparedFieldBase = {
        ignored: field.ignored,
        notPersisted: field.notPersisted,
        editable: field.editable,
        visible: field.visible,
        group: field.group,
        type: field.type,
        title: field.title,
        name: field.field_name,
        isAutocomplete: field.autocomplete,
        disable_input: field.disable_input,
        isRange: field.min_max,
        orderable: !!(field.sort_field && field.sort_field.length),
        hasMultipleValues: field.multiple_values,
        order: field.builder_order,
        minQueryLength: field.minimum_input_length === 1 ? 0 : field.minimum_input_length,
        visibleConditions: field.visible_conditions ? field.visible_conditions : [],
        maxOptions: field.num_options,
        tooltip: field.tooltip,
        description: field.description,
        isInline: field.inline,
    };

    if (field.allow_create && field.model) {
        prepared.canCreate = field.allow_create;
        prepared.model = field.model;
        prepared.relationSubviewName = identify.subview({ 'model': field.model, 'filterValue': field.field_name, "filterName": 'id' });
    }

    if (field.constraints) {
        prepared.constraints = field.constraints;
    }
    
    if (field.options) {
        prepared.options = prepareOptions(field.options);
    }

    if (field.selections) {
        prepared.selections = prepareSelections(field.selections);
    }

    return prepared;
};


const prepareValueAdapt = (field, prepared: SetOptional<PreparedField, 'adaptValue'>, type) => {
    if (field.adapt_to_filter) {
        prepared = {
            ...prepared,
            adaptValue: {
                field: field.adapt_to_filter,
                mode: identify.VALUE_ADAPT_SUBSTITUTE,
                inputType: identify.FILTERFIELD,
            },
        };
    }

    if (field.adapt_value) {
        prepared = {
            ...prepared,
            adaptValue: {
                option: field.adapt_value.option,
                field: field.adapt_value.field,
                mode: field.adapt_value.mode ? field.adapt_value.mode : identify.VALUE_ADAPT_OVERWRITE,
                inputType: field.adapt_value.inputType ? field.adapt_value.inputType : type,
            },
        };
    }

    return prepared  as PreparedField;
};



export const prepareFilter = (filter) => {
    return prepareValueAdapt(filter, {
        ...prepareField(filter),
        inputType: identify.FILTERFIELD as PreparedField['inputType'],
    }, identify.FILTERFIELD);
};




export const prepareEditField = (editField) => {
    let prepared: SetOptional<PreparedField, 'adaptValue'> = {
        ...prepareField(editField),
        inputType: identify.EDITFIELD as PreparedField['inputType'],
        publicPath: editField.public_path,
        isRange: false,
    };

    prepared = prepareValueAdapt(editField, prepared, identify.EDITFIELD);

    return prepared as PreparedField;
};

export const prepareOptions = (options): FieldOptionsPrepared[] => {
    return options.map((option) => ({
        ...omit(option, ['id']),
        value: parseInt(option.id),
    }));
};

export const prepareSelections = (selections: FieldType['selections']) => entries(selections)
    .map(([key, {icon_family, ...option}]) => ({
        ...option,
        iconFamily: icon_family,
        value: isNaN(+key) ? key : +key,
    })).sort((a, b) => a.builder_order - b.builder_order);


export const prepareRelationFieldSubviews = (fields: Record<string, PreparedField>, sources) => {
    return prepareSubviews(
            Object.values(fields)
            .filter((field) => field.canCreate && field.model)
            .map((field) => prepareSubview({
                visibleColumns: [],
                visible_conditions: { id: "false" },
                model: field.model,
                filterName: "id",
                filterValue: field.name,
                title: '',
                hiddenColumns: [],
                hiddenFields: [],
                relationField: field.name,
                group: field.group,
                builder_order: sources.includes(field.group) ? 0 : 1
            })
        ).sort((a, b) => a.builder_order - b.builder_order)
    )
}